<template>
  <div class="grid">
    <div v-if="showSqlCode" class="col-12">
      <SQLSelector
        ref="sqlSelector"
        :filters="filters"
        :editFilter="editFilter"
        :disabledVars="disabledVars"
        :isValorDeRango="isValorDeRango"
        :pickingMinOrMax="pickingMinOrMax"
        :preselectedVariable="preselectedVariable"
        @selectString="(variable) => setDisabledMinMaxVariable(variable)"
      />
      <hr class="horizontal-row-secondary mb-1" />
    </div>
    <!-- Tipo -->
    <div class="col-12 sm:col-6 md:col-4">
      <label for="tipo" class="block mb-2">Tipo</label>
      <Dropdown
        v-model="tipo"
        inputId="tipo"
        :options="tipos"
        optionValue="value"
        optionLabel="name"
        class="w-full"
      />
      <small v-if="tipoError" class="p-error">{{ tipoError }}</small>
    </div>

    <!-- Clase -->
    <div v-if="tipo != null" class="col-12 sm:col-6 md:col-4">
      <label for="clase" class="block mb-2">Clase</label>
      <Dropdown
        v-model="clase"
        inputId="clase"
        :options="clases"
        optionValue="value"
        optionLabel="name"
        class="w-full"
      />
      <small v-if="claseError" class="p-error">{{ claseError }}</small>
    </div>

    <div class="col-12 sm:col-6 md:col-4" v-if="isListaDeValores">
      <label class="block mb-2">&nbsp;</label>
      <div class="flex align-items-center">
        <Button label="Añadir valor" @click="listaDeValoresTotal++" />
      </div>
    </div>

    <div class="col-12 sm:col-6 md:col-4" v-if="isValorDeRango">
      <label class="block mb-2">&nbsp;</label>
      <SelectButton
        v-model="pickingMinOrMax"
        :options="['Valor mínimo', 'Valor máximo']"
      />
    </div>

    <!-- Grafico asociado -->
    <div v-if="reportType == reportTypes.Grafico" class="col-12 md:col-4 sm:col-6">
      <label for="grafico" class="block mb-2">Gráfico asociado</label>
      <Dropdown
        v-model="grafico"
        inputId="grafico"
        :options="graphs"
        optionValue="nombre"
        optionLabel="titulo"
        showClear
        class="w-full"
        :disabled="graphs.length == 0"
      />
    </div>

    <!-- Ubicación -->
    <div class="col-12 sm:col-6 md:col-4">
      <label for="ubicacion" class="block mb-2">Ubicación</label>
      <Dropdown
        v-model="ubicacion"
        inputId="ubicacion"
        :options="ubicaciones"
        optionValue="name"
        optionLabel="value"
        class="w-full"
      />
      <small v-if="ubicacionError" class="p-error">{{ ubicacionError }}</small>
    </div>

    <!-- Nombre -->
    <div class="col-12 sm:col-6 md:col-4">
      <label for="nombre" class="block mb-2">Nombre</label>
      <InputText v-model="nombre" inputId="nombre" class="w-full" />
      <small v-if="nombreError" class="p-error">{{ nombreError }}</small>
    </div>

    <!-- Título -->
    <div class="col-12 sm:col-6 md:col-4">
      <label for="titulo" class="block mb-2">Título</label>
      <InputText v-model="titulo" inputId="titulo" class="w-full" />
      <small v-if="tituloError" class="p-error">{{ tituloError }}</small>
    </div>

    <!-- Regex -->
    <div
      class="col-12 sm:col-6 md:col-4"
      :class="{
        hidden: (isValorDeRango || isListaDeValores) && tipo == 3,
      }"
    >
      <label for="regex" class="block mb-2">Regex</label>
      <InputText v-model="regex" inputId="regex" class="w-full" />
    </div>

    <!-- Valor predeterminado -->
    <div class="col-12 sm:col-6 md:col-4">
      <label for="predValue" class="block mb-2">{{ predValuePlaceholder }}</label>
      <template v-if="clase == 'Lista de valores' && multipleSelection">
        <MultiSelect
          v-model="predValue"
          inputId="predValue"
          :options="predValueOptions"
          optionLabel="clave"
          optionValue="valor"
          class="w-full"
          :disabled="predValueOptions.length == 0"
          display="chip"
        />
      </template>
      <template v-else>
        <InputNumber
          v-if="tipo == 1 || tipo == 2"
          v-model="predValue"
          inputId="predValue"
          class="w-full"
          :maxFractionDigits="tipo == 1 ? 0 : 5"
        />
        <Calendar
          v-if="tipo == 3"
          v-model="predValue"
          inputId="predValue"
          class="w-full"
        />
        <InputText
          v-if="tipo == 4"
          v-model="predValue"
          inputId="predValue"
          class="w-full"
        />
      </template>
      <small v-if="predValueError" class="p-error">{{ predValueError }}</small>
    </div>

    <div class="col-12 sm:col-6 md:col-4" v-if="isListaDeValores">
      <label class="block mb-2">&nbsp;</label>
      <div class="flex flex-row gap-1 align-items-center">
        <label>Selección múltiple</label>
        <InputSwitch v-model="multipleSelection" />
      </div>
    </div>

    <!-- Valor predeterminado máximo -->
    <div v-if="claseIsValorDeRango" class="col-12 md:col-4 sm:col-6">
      <label for="maxPredValue" class="block mb-2">Valor predeterminado máximo</label>
      <InputNumber
        v-if="tipo == 1 || tipo == 2"
        v-model="maxPredValue"
        inputId="maxPredValue"
        :maxFractionDigits="tipo == 1 ? 0 : 5"
        class="w-full"
      />
      <Calendar
        v-if="tipo == 3"
        v-model="maxPredValue"
        inputId="maxPredValue"
        class="w-full"
      />
      <InputText
        v-if="tipo == 4"
        v-model="maxPredValue"
        inputId="maxPredValue"
        class="w-full"
      />
      <small v-if="maxPredValueError" class="p-error">{{
        maxPredValueError
      }}</small>
    </div>

    <!-- Min -->
    <template v-if="isValorDeRango">
      <div class="col-12 md:col-4 sm:col-6">
        <label for="min" class="block mb-2">Valor mínimo posible</label>
        <InputNumber
          v-if="tipo == 1 || tipo == 2"
          v-model="min"
          inputId="min"
          class="w-full"
          :maxFractionDigits="tipo == 1 ? 0 : 5"
        />
        <Calendar
          v-if="tipo == 3"
          v-model="min"
          inputId="min"
          class="w-full"
        />
      </div>

      <!-- Max -->
      <div class="col-12 md:col-4 sm:col-6">
        <label for="max" class="block mb-2">Valor máximo posible</label>
        <InputNumber
          v-if="tipo == 1 || tipo == 2"
          v-model="max"
          inputId="max"
          class="w-full"
          :maxFractionDigits="tipo == 1 ? 0 : 5"
        />
        <Calendar
          v-if="tipo == 3"
          v-model="max"
          inputId="max"
          class="w-full"
        />
      </div>
    </template>

    <!-- Lista de valores -->
    <div v-if="tipo != null && isListaDeValores" class="col-12">
      <div class="values-list-container">
        <div
          v-for="(n, index) in listaDeValoresTotal"
          :key="n"
          class="grid"
          :class="{ 'mt-1': index > 0 }"
        >
          <div class="col">
            <label :for="`key-${n}`" class="block mb-2">Nombre interfaz</label>
            <InputText
              v-model="keys[n - 1]"
              :inputId="`key-${n}`"
              class="w-full"
            />
          </div>
          <div class="col">
            <label :for="`value-${n}`" class="block mb-2">Valor SQL</label>
            <InputNumber
              v-if="tipo == 1 || tipo == 2"
              v-model="values[n - 1]"
              :inputId="`value-${n}`"
              class="w-full"
              :maxFractionDigits="tipo == 1 ? 0 : 5"
            />
            <Calendar
              v-if="tipo == 3"
              v-model="values[n - 1]"
              :inputId="`value-${n}`"
              class="w-full"
              :manualInput="false"
            />
            <InputText
              v-if="tipo == 4"
              v-model="values[n - 1]"
              :inputId="`value-${n}`"
              class="w-full"
            />
          </div>
          <div class="col-2 md:col-1">
            <label class="block mb-2">&nbsp;</label>
            <Button
              icon="pi pi-trash"
              @click="removeKeyValue(n)"
              class="w-full"
              severity="danger"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Añadir -->
    <div class="col-12 flex justify-content-end gap-2">
      <Button label="Cancelar" @click="cancel()" severity="secondary" />
      <Button
        :label="props.editFilter ? 'Actualizar' : 'Añadir'"
        @click="save()"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed, watch } from "vue";
import { useStore } from "vuex";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import InputSwitch from "primevue/inputswitch";
import Button from "primevue/button";
import SelectButton from "primevue/selectbutton";
import MultiSelect from "primevue/multiselect";

import SQLSelector from "@/components/partials/SQLSelector.vue";
import { reportTypes } from "@/composables/_commons/enums.js";

// #region Props, Emits
const emit = defineEmits(["saveFilter", "cancel"]);

const props = defineProps({
  filters: {
    type: Array,
    default: () => [],
  },
  reportType: {
    type: String,
    default: null,
  },
  editFilter: {
    type: Object,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const sqlSelector = ref(null);
const store = useStore();
const keys = ref([]);
const values = ref([]);

const loadingData = ref(false);
const nombre = ref(null);
const nombreError = ref(null);
const titulo = ref(null);
const tituloError = ref(null);
const tipo = ref(1);
const tipoError = ref(null);
const ubicacion = ref("Superior");
const ubicacionError = ref(null);
const predValue = ref(null);
const maxPredValue = ref(null);
const predValueError = ref(null);
const maxPredValueError = ref(null);
const regex = ref(null);
const grafico = ref(null);
const clase = ref("Valor único");
const claseError = ref(null);
const min = ref(null);
const max = ref(null);
const multipleSelection = ref(false);
const listaDeValoresTotal = ref(0);
const pickingMinOrMax = ref("Valor mínimo");
const preselectedVariable = ref(null);
const predValueOptions = ref([]);
const disabledVars = ref([
  {
    type: "min",
    value: null,
  },
  {
    type: "max",
    value: null,
  },
]);

const tipos = [
  {
    name: "Entero",
    value: 1,
  },
  {
    name: "Decimal",
    value: 2,
  },
  {
    name: "Fecha",
    value: 3,
  },
  {
    name: "Texto",
    value: 4,
  },
];

const ubicaciones = [
  {
    name: "Superior",
    value: "Superior",
  },
  {
    name: "Lateral",
    value: "Lateral",
  },
];

const isValorDeRango = computed(() => {
  disabledVars.value = [
    {
      type: "min",
      value: null,
    },
    {
      type: "max",
      value: null,
    },
  ];
  return clase.value == "Valor de rango";
});

const isListaDeValores = computed(() => {
  return clase.value == "Lista de valores";
});

const clases = computed(() => {
  const values = [
    {
      name: "Valor único",
      value: "Valor único",
    },
    {
      name: "Lista de valores",
      value: "Lista de valores",
    },
  ];

  if (tipo.value == 4) {
    return values;
  } else {
    values.splice(1, 0, {
      name: "Valor de rango",
      value: "Valor de rango",
    });

    return values;
  }
});

const sqlCode = computed(() => store.getters.sqlCode);
const graphs = computed(() => store.getters.graphs);
const showSqlCode = computed(() => {
  return props.reportType == reportTypes.Tabla && sqlCode.value;
});
const claseIsValorDeRango = computed(() => clase.value == "Valor de rango");
const predValuePlaceholder = computed(() => {
  return claseIsValorDeRango.value
    ? "Valor predeterminado mínimo"
    : "Valor predeterminado";
});
// #endregion

// #region Functions
function getIdFiltro() {
  let idFiltro;

  if (props.editFilter != null) {
    idFiltro = props.editFilter.idFiltro;
  } else if (props.filters.length == 0) {
    idFiltro = 1;
  } else {
    idFiltro = props.filters[props.filters.length - 1].idFiltro + 1;
  }

  return idFiltro;
}

function formatPredValue() {
  if (
    typeof predValue.value === "object" &&
    !(predValue.value instanceof Date)
  ) {
    predValue.value.forEach((value, index) => {
      if (value instanceof Date) {
        predValue.value[index] = new Date(value);
      } else {
        predValue.value[index] = value.toString();
      }
    });
  } else {
    predValue.value = predValue.value.toString();
  }
}

function formatFilter(filter) {
  if (filter.claseFiltroDto) {
    const clase = filter.claseFiltroDto;

    if (clase.esValorUnico) {
      filter.valorPredeterminado = clase.predValue.toString();
    }

    if (clase.esListaValores) {
      if (multipleSelection.value) {
        filter.valorPredeterminado = Object.values(clase.predValue);
      } else {
        filter.valorPredeterminado = [clase.predValue.toString()];
      }
    }

    if (clase.esValorRango) {
      filter.valorPredeterminado = [
        clase.predValue.toString(),
        clase.maxPredValue.toString(),
      ];
    }

    if (typeof filter.valorPredeterminado !== "object") {
      filter.valorPredeterminado = [filter.valorPredeterminado];
    }
  }

  if (showSqlCode.value) {
    if (isValorDeRango.value) {
      filter.variable = disabledVars.value;
    } else {
      filter.variable = sqlSelector.value.selectedOption?.value;
    }
  }

  if (clase.value == "Valor de rango") {
    filter.variable = JSON.stringify(filter.variable);
  }
}

function save() {
  if (validateForm()) {
    return;
  }

  const idFiltro = getIdFiltro();
  formatPredValue();

  predValueOptions.value.forEach((options, index) => {
    predValueOptions.value[index].valor = options.valor.toString();
  });

  const filter = {
    idFiltro,
    nombre: nombre.value,
    titulo: titulo.value,
    idTipoFiltro: tipo.value,
    ubicacion: ubicacion.value,
    claseFiltroDto: {
      nombreClaseFiltro: clase.value,
      valorMin: min.value?.toString(),
      valorMax: max.value?.toString(),
      predValue: predValue.value,
      maxPredValue: maxPredValue.value,
      valorRegex: regex.value,
      esValorUnico: clase.value == "Valor único",
      esValorRango: clase.value == "Valor de rango",
      esListaValores: clase.value == "Lista de valores",
      valores: predValueOptions.value,
      tieneSeleccionMultiple: multipleSelection.value,
    },

    tipoFiltroDto: {
      idTipoFiltro: tipo.value,
      nombreTipoFiltro: tipos.find((t) => t.value == tipo.value).name,
    },
  };

  formatFilter(filter);

  nextTick(() => {
    emit("save-filter", filter);
  });
}

function checkListaDeValores() {
  const keyValueErrorText = "Todos los pares clave-valor deben tener datos";

  if (clase.value == "Lista de valores") {
    if (listaDeValoresTotal.value == 0) {
      claseError.value = "Debe añadir al menos un valor";
    } else {
      if (
        keys.value.length == 0 ||
        listaDeValoresTotal.value != keys.value.length
      ) {
        claseError.value = keyValueErrorText;
      } else {
        keys.value.forEach((key) => {
          if (!key || (typeof key === "string" && !key.trim())) {
            claseError.value = keyValueErrorText;
          }
        });
      }

      if (
        values.value.length == 0 ||
        listaDeValoresTotal.value != values.value.length
      ) {
        claseError.value = keyValueErrorText;
      } else {
        values.value.forEach((value) => {
          if (!value || (typeof value === "string" && !value.trim())) {
            claseError.value = keyValueErrorText;
          }
        });
      }
    }
  }
}

function validateForm() {
  let error = false;

  nombreError.value = null;
  tituloError.value = null;
  tipoError.value = null;
  ubicacionError.value = null;
  claseError.value = null;
  predValueError.value = null;
  maxPredValueError.value = null;

  if (!nombre.value?.trim()) {
    nombreError.value = "El nombre es requerido";
    error = true;
  }

  if (!titulo.value?.trim()) {
    tituloError.value = "El título es requerido";
    error = true;
  }

  if (!tipo.value) {
    tipoError.value = "El tipo es requerido";
    error = true;
  }

  if (!ubicacion.value) {
    ubicacionError.value = "La ubicación es requerida";
    error = true;
  }

  if (!predValue.value?.toString()) {
    predValueError.value = "El valor predeterminado es requerido";

    if (claseIsValorDeRango.value) {
      predValueError.value = "El valor predeterminado mínimo es requerido";
    }

    error = true;
  }

  if (!maxPredValue.value?.toString() && claseIsValorDeRango.value) {
    maxPredValueError.value = "El valor predeterminado máximo es requerido";
    error = true;
  }

  if (!clase.value) {
    claseError.value = "La clase es requerida";
    error = true;
  }

  checkListaDeValores();

  if (claseError.value) {
    error = true;
  }

  return error;
}

function removeKeyValue(n) {
  keys.value.splice(n - 1, 1);
  values.value.splice(n - 1, 1);

  listaDeValoresTotal.value--;
}

function cancel() {
  emit("cancel");
}

function setDisabledMinMaxVariable(variable) {
  if (pickingMinOrMax.value == "Valor mínimo") {
    disabledVars.value[0].value = variable;
  } else {
    disabledVars.value[1].value = variable;
  }
}

function formatPredValueOptions() {
  predValue.value = null;
  let options = [];

  for (let index = 0; index < listaDeValoresTotal.value; index++) {
    let key = null;
    let value = null;

    keys.value.forEach((k, i) => {
      if (i == index) {
        key = k;
      }
    });

    values.value.forEach((v, i) => {
      if (i == index) {
        value = v;
      }
    });

    if (key && value) {
      options.push({
        clave: key,
        valor: tipo.value == 1 || tipo.value == 2 ? value.toString() : value,
      });
    }
  }

  predValueOptions.value = options;
}

function onTipoChange(newValue) {
  if (newValue == 4) {
    clase.value = "Valor único";
  }

  predValue.value = null;
  maxPredValue.value = null;
  min.value = null;
  max.value = null;
  predValueOptions.value = [];
  listaDeValoresTotal.value = 0;
  keys.value = [];
  values.value = [];
}

function loadVariable(filter) {
  if (filter.variable) {
    if (Array.isArray(filter.variable)) {
      disabledVars.value = filter.variable;
    }

    preselectedVariable.value = filter.variable;
  }
}

async function loadClaseFiltro(claseFiltro) {
  if (claseFiltro) {
    if (claseFiltro.tieneSeleccionMultiple) {
      multipleSelection.value = true;
    }

    if (claseFiltro.valorRegex) {
      regex.value = claseFiltro.valorRegex;
    }

    if (claseFiltro.valores) {
      await nextTick();

      if (claseFiltro.valores) {
        const valores = claseFiltro.valores;
        listaDeValoresTotal.value = valores.length;

        valores.forEach((valor) => {
          keys.value.push(valor.clave);
          values.value.push(
            tipo.value == 3 ? new Date(valor.valor) : 
              tipo.value == 1 || tipo.value == 2 ? Number(valor.valor) : valor.valor,
          );
        });
      }
    }

    if (claseFiltro.valorMin) {
      min.value =
        tipo.value == 3 ? new Date(claseFiltro.valorMin) : 
          tipo.value == 1 || tipo.value == 2 ? Number(claseFiltro.valorMin) : claseFiltro.valorMin;
    }

    if (claseFiltro.valorMax) {
      max.value =
        tipo.value == 3 ? new Date(claseFiltro.valorMax) : 
          tipo.value == 1 || tipo.value == 2 ? Number(claseFiltro.valorMax) : claseFiltro.valorMax;
    }
  }
}

function loadValorPredeterminado(valorPredeterminado, claseFiltro) {
  if (valorPredeterminado.length <= 2) {
    predValue.value =
      tipo.value == 3
        ? new Date(valorPredeterminado[0])
        : tipo.value == 1 || tipo.value == 2 ? Number(valorPredeterminado[0]) :
          valorPredeterminado[0];
  }

  if (valorPredeterminado.length > 1) {
    maxPredValue.value =
      tipo.value == 3
        ? new Date(valorPredeterminado[1])
        : tipo.value == 1 || tipo.value == 2 ? Number(valorPredeterminado[1]) :
          valorPredeterminado[1];
  }

  if (valorPredeterminado.length > 2) {
    predValue.value = valorPredeterminado;
  }

  if (
    claseFiltro.nombreClaseFiltro == "Lista de valores" &&
    multipleSelection.value
  ) {
    predValue.value = valorPredeterminado;

    if (tipo.value == 3) {
      predValue.value = valorPredeterminado.map((v) => new Date(v));
    }
  }
}
// #endregion

// #region Effects, Hooks
onMounted(async () => {
  if (props.editFilter) {
    loadingData.value = true;

    const filter = props.editFilter;
    const claseFiltro = filter.claseFiltroDto;

    nombre.value = filter.nombre;
    titulo.value = filter.titulo;
    ubicacion.value = filter.ubicacion;

    if (!filter.idTipoFiltro) {
      tipo.value = filter.tipoFiltroDto.idTipoFiltro;
    } else {
      tipo.value = filter.idTipoFiltro;
    }

    await nextTick();

    if (filter.claseFiltroDto?.nombreClaseFiltro) {
      clase.value = filter.claseFiltroDto.nombreClaseFiltro;
    }

    if (clase.value == "Valor de rango") {
      filter.variable = JSON.parse(filter.variable);
    }

    await nextTick();

    loadVariable(filter);
    await loadClaseFiltro(claseFiltro);

    await nextTick();

    formatPredValueOptions();

    let valorPredeterminado = Object.values(filter.valorPredeterminado);

    if (valorPredeterminado) {
      loadValorPredeterminado(valorPredeterminado, claseFiltro);
    }

    loadingData.value = false;
  }

  if (showSqlCode.value) {
    sqlSelector.value.formatSqlCode();
  }
});

watch(listaDeValoresTotal, () => {
  formatPredValueOptions();
});

watch(
  [keys, values],
  () => {
    if (!loadingData.value) {
      formatPredValueOptions();
    }
  },
  { deep: true },
);

watch(clase, () => {
  if (!loadingData.value) {
    formatPredValueOptions();
    onTipoChange(tipo.value);

    if (showSqlCode.value) {
      preselectedVariable.value = null;
      sqlSelector.value.formatSqlCode();
    }
  }
});

watch(tipo, (newValue) => {
  if (!loadingData.value) {
    onTipoChange(newValue);
  }
});

watch(multipleSelection, () => {
  predValue.value = null;
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
.values-list-container {
  max-height: 20rem;
  overflow-y: auto;
  overflow-x: hidden;
}

label {
  font-weight: bold;
  font-size: 0.80rem;
}
</style>
